import React, { useState, useEffect } from 'react';
import { ModeSwitch } from '../components/player/ModeSwitch';
import { PlayButton3D } from '../components/player/PlayButton3D';
import { PKIFlow } from '../components/freundebuch/PKIFlow';
import { LoadingAnimation3D } from '../components/common/LoadingAnimation3D';
import { PKIToolbar } from '../components/player/PKIToolbar';
import { TicTacToe3D } from '../components/activities/TicTacToe3D';
import { DrawingCanvas3D } from '../components/activities/DrawingCanvas3D';
import { PKIDebugPanel } from '../components/debug/PKIDebugPanel';
import { TrackVisualizer } from '../components/player/TrackVisualizer';

/**
 * Section 1 - Player/Analyzer Section
 * Displays the interactive player with analyzer visualization
 */
export function PlayerSection({ currentSection, scrollProgress, isAnalyzerActive, isStreamLoading, isStreamOffline, currentTrack, isPKIActive, pkiCurrentQuestion, pkiCompleted, currentProfileId, mode, onModeChange, onPlayClick, onPKIComplete, onPKIQuestionChange, onJoinStream }) {
  const [activeActivity, setActiveActivity] = useState(null);
  const [activityStatus, setActivityStatus] = useState('');
  const [hasPlayedTicTacToe, setHasPlayedTicTacToe] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [pkiCurrentPage, setPkiCurrentPage] = useState(0);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joinStreamId, setJoinStreamId] = useState('');
  const [copiedId, setCopiedId] = useState(false);

  const copyStreamId = async () => {
    if (currentProfileId) {
      try {
        // Try modern clipboard API first
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(currentProfileId);
        } else {
          // Fallback for mobile/HTTP
          const textArea = document.createElement('textarea');
          textArea.value = currentProfileId;
          textArea.style.position = 'fixed';
          textArea.style.left = '-9999px';
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }
        setCopiedId(true);
        setTimeout(() => setCopiedId(false), 2000);
      } catch (err) {
        console.error('Copy failed:', err);
      }
    }
  };

  // Reset activity when switching to KI mode
  useEffect(() => {
    if (mode === 'KI') {
      setActiveActivity(null);
      setActivityStatus('');
    }
  }, [mode]);


  const handleJoinSubmit = () => {
    if (joinStreamId.trim()) {
      onJoinStream(joinStreamId.trim());
      setShowJoinModal(false);
      setJoinStreamId('');
    }
  };

  // Status text logic
  const getStatusText = () => {
    if (activeActivity && activityStatus) return activityStatus;
    if (isStreamLoading) return 'Stream wird geladen...';
    if (isAnalyzerActive && currentTrack) return currentTrack;
    return null;
  };

  const statusText = getStatusText();
  const showClickToPlay = !activeActivity && !isPKIActive && !isAnalyzerActive && !isStreamLoading;

  return (
    <div
      id="player"
      className={`w-full relative pointer-events-none flex items-start sm:items-center justify-center pt-8 sm:pt-0 transition-all duration-500 ${
        (isAnalyzerActive || isPKIActive || isStreamLoading)
          ? 'min-h-[60vh] sm:min-h-screen sm:h-screen'
          : 'min-h-screen sm:h-screen'
      }`}
      style={{
        scrollSnapAlign: 'start',
        scrollSnapStop: 'always',
        overflow: 'hidden',
        maxWidth: '100vw'
      }}
    >
      {/* Main Player Card - contains everything */}
      <div className="pointer-events-auto px-4 w-full max-w-7xl mx-auto">
          {/* Section Heading */}
          <div className="flex flex-col items-center mb-6 sm:mb-10">
            <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-center text-white">
              Neuer. Besser. <span className="text-pink-500">ICH.</span>
            </h2>
            <svg
              viewBox="0 0 300 20"
              preserveAspectRatio="none"
              className="w-full h-4 sm:h-5 mt-2"
              style={{ maxWidth: '100%' }}
            >
              <defs>
                <linearGradient id="accentGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity="0" />
                  <stop offset="15%" stopColor="#3b82f6" stopOpacity="1" />
                  <stop offset="50%" stopColor="#a855f7" stopOpacity="1" />
                  <stop offset="85%" stopColor="#ec4899" stopOpacity="1" />
                  <stop offset="100%" stopColor="#ec4899" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path
                d="M0,10 C50,18 100,2 150,10 C200,18 250,2 300,10"
                fill="none"
                stroke="url(#accentGradient)"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
          {/* Main Player Card */}
          <div
            className={`relative rounded-3xl sm:rounded-[2.5rem] p-6 sm:p-10 lg:p-12 overflow-hidden flex flex-col sm:h-[590px] ${
              (!isAnalyzerActive && !isPKIActive && !isStreamLoading)
                ? 'h-[140vh]'
                : (isPKIActive && !isAnalyzerActive && !isStreamLoading)
                  ? ((pkiCurrentPage === 4 || pkiCurrentPage === 6) ? 'h-[92vh]' : pkiCurrentPage === 7 ? 'h-[85vh]' : pkiCurrentPage === 8 ? 'h-[105vh]' : 'h-[75vh]')
                  : activeActivity === 'ticTacToe'
                    ? 'h-[80vh]'
                    : 'h-[70vh]'
            }`}
            style={{
              transition: 'height 0.5s ease-in-out',
              background: 'linear-gradient(135deg, rgba(239, 246, 255, 0.95) 0%, rgba(255, 255, 255, 0.95) 50%, rgba(253, 242, 248, 0.95) 100%)',
              backdropFilter: 'blur(20px)'
            }}
          >
            {/* Track Visualizer - full width, in upper area */}
            <div
              className={`absolute left-0 right-0 pointer-events-none ${isPKIActive && isAnalyzerActive ? 'top-[45%]' : isAnalyzerActive ? 'top-[43%]' : 'top-[30%]'} sm:top-[39%]`}
              style={{ height: '300px', transform: 'translateY(-50%)' }}
            >
              <TrackVisualizer track={currentTrack} isPlaying={isAnalyzerActive} />
            </div>

            {/* Share Stream ID Button - top left when PKI stream is playing */}
            {isPKIActive && isAnalyzerActive && currentProfileId && (
              <button
                onClick={copyStreamId}
                className="absolute top-4 left-4 sm:top-6 sm:left-6 z-20 w-10 h-10 flex items-center justify-center bg-white/80 hover:bg-white rounded-full transition-all duration-200"
              >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className={copiedId ? "text-green-500" : "text-gray-600"}>
                  <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </button>
            )}

            {/* PKI Toolbar - Mobile: top right */}
            {isPKIActive && isAnalyzerActive && (
              <div className="sm:hidden absolute top-4 right-4 z-20">
                <PKIToolbar
                  onOpenTicTacToe={() => setActiveActivity('ticTacToe')}
                  onOpenDrawing={() => setActiveActivity('drawing')}
                  currentSection={currentSection}
                  scrollProgress={scrollProgress}
                />
              </div>
            )}

            {/* Join Stream Modal */}
            {showJoinModal && (
              <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/20 rounded-3xl sm:rounded-[2.5rem]">
                <div className="bg-white rounded-2xl p-6 shadow-2xl w-[90%] max-w-sm mx-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900">Stream beitreten</h3>
                    <button
                      onClick={() => {
                        setShowJoinModal(false);
                        setJoinStreamId('');
                      }}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Füge die Stream-ID ein, die du von einem Freund erhalten hast.
                  </p>
                  <input
                    type="text"
                    value={joinStreamId}
                    onChange={(e) => setJoinStreamId(e.target.value)}
                    placeholder="Stream-ID eingeben..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-gray-900 mb-4"
                    onKeyDown={(e) => e.key === 'Enter' && handleJoinSubmit()}
                    autoFocus
                  />
                  <button
                    onClick={handleJoinSubmit}
                    disabled={!joinStreamId.trim()}
                    className="w-full py-3 bg-gradient-to-r from-blue-500 to-pink-500 text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Beitreten
                  </button>
                </div>
              </div>
            )}

            {/* Main Row: Left Card - Play Button/PKI Flow - Right Cards */}
            <div className="flex-1 min-h-0 flex flex-col sm:flex-row items-stretch gap-6 sm:gap-8 lg:gap-12 relative z-10">

              {/* Mobile: Cards ABOVE button - animate out when playing */}
              <div
                className="flex sm:hidden flex-col gap-3 order-first overflow-hidden transition-all duration-500 ease-in-out"
                style={{
                  maxHeight: (!isAnalyzerActive && !isPKIActive && !isStreamLoading) ? '1000px' : '0px',
                  opacity: (!isAnalyzerActive && !isPKIActive && !isStreamLoading) ? 1 : 0,
                  marginBottom: (!isAnalyzerActive && !isPKIActive && !isStreamLoading) ? undefined : '-1.5rem'
                }}
              >
                <div className="bg-white rounded-2xl p-6 text-center">
                  <div className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-pink-500 text-xs lg:text-sm font-semibold mb-3 uppercase tracking-wider">
                    So einfach geht's
                  </div>
                  <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3">Wie funktioniert's?</h3>
                  <p className="text-sm lg:text-base text-gray-600 leading-relaxed">
                    Der Play-Button startet das Radio und du hörst deinen ausgewählten Stream. Wähle zwischen KI oder PKI, um genau das zu hören, was du gerade brauchst.
                  </p>
                </div>
                <div className="flex flex-col gap-3">
                  <div className="bg-white rounded-2xl p-4 text-center">
                    <div className="text-blue-500 text-xs lg:text-sm font-semibold mb-1 uppercase tracking-wider">Der KI-Modus</div>
                    <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-1">Einfach zurücklehnen</h3>
                    <p className="text-sm lg:text-base text-gray-600 leading-relaxed">
                      Du hörst das Radio, das alle hören. Die KI kümmert sich für dich um alles.
                    </p>
                  </div>
                  <div className="bg-white rounded-2xl p-4 text-center">
                    <div className="text-pink-500 text-xs lg:text-sm font-semibold mb-1 uppercase tracking-wider">Der PKI-Modus</div>
                    <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-1">Dein persönliches Radio</h3>
                    <p className="text-sm lg:text-base text-gray-600 leading-relaxed">
                      Beantworte ein paar einfache Fragen, und die schmalsoft-KI erstellt dir daraus dein perfektes personalisiertes Programm.
                    </p>
                  </div>
                </div>
              </div>

              {/* Left: Wie funktioniert es Card */}
              {(!isAnalyzerActive && !isPKIActive && !isStreamLoading) && (
              <div
                className="hidden sm:flex flex-col justify-center bg-white rounded-2xl p-6 lg:p-8 text-center w-full sm:w-[280px] lg:w-[320px] xl:w-[380px]"
              >
                <div className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-pink-500 text-xs lg:text-sm font-semibold mb-3 uppercase tracking-wider">
                  So einfach geht's
                </div>
                <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3">
                  Wie funktioniert es?
                </h3>
                <p className="text-sm lg:text-base text-gray-600 leading-relaxed">
                  Der Play-Button startet das Radio und du hörst deinen ausgewählten Stream. Wähle zwischen KI oder PKI, um genau das zu hören, was du gerade brauchst.
                </p>
              </div>
              )}

              {/* Center: Play Button OR PKI Flow OR Activities */}
              <div className={`flex-1 flex items-center justify-center ${(isAnalyzerActive || isStreamLoading) ? 'min-h-[50vh] sm:min-h-0' : ''}`}>
                {activeActivity === 'ticTacToe' ? (
                  <TicTacToe3D
                    profileId={currentProfileId}
                    onClose={() => {
                      setActiveActivity(null);
                      setActivityStatus('');
                      setHasPlayedTicTacToe(true);
                    }}
                    onStatusChange={setActivityStatus}
                  />
                ) : activeActivity === 'drawing' ? (
                  <DrawingCanvas3D
                    profileId={currentProfileId}
                    onClose={() => {
                      setActiveActivity(null);
                      setActivityStatus('');
                      setHasDrawn(true);
                    }}
                    onStatusChange={setActivityStatus}
                  />
                ) : isStreamLoading ? (
                  <LoadingAnimation3D isPKI={isPKIActive} />
                ) : isPKIActive && !isAnalyzerActive && !pkiCompleted ? (
                  <PKIFlow
                    isActive={isPKIActive}
                    onComplete={onPKIComplete}
                    onQuestionChange={onPKIQuestionChange}
                    onPageChange={setPkiCurrentPage}
                    isStreamPlaying={isAnalyzerActive}
                    onPlayPauseClick={onPlayClick}
                    onJoinClick={() => setShowJoinModal(true)}
                  />
                ) : (
                  <PlayButton3D onClick={onPlayClick} isLoading={false} isPlaying={isAnalyzerActive} isPKI={isPKIActive} />
                )}
              </div>

              {/* Right: KI & PKI Cards */}
              {(!isAnalyzerActive && !isPKIActive && !isStreamLoading) && (
              <div
                className="hidden sm:flex flex-col gap-4 w-full sm:w-[280px] lg:w-[320px] xl:w-[380px]"
              >
                {/* KI Card */}
                <div className="flex-1 bg-white rounded-2xl p-5 lg:p-6 text-center flex flex-col justify-center">
                  <div className="text-blue-500 text-xs lg:text-sm font-semibold mb-2 uppercase tracking-wider">
                    Der KI-Modus
                  </div>
                  <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-2">
                    Einfach zurücklehnen
                  </h3>
                  <p className="text-sm lg:text-base text-gray-600 leading-relaxed">
                    Du hörst das Radio, das alle hören. Die KI kümmert sich für dich um alles.
                  </p>
                </div>

                {/* PKI Card */}
                <div className="flex-1 bg-white rounded-2xl p-5 lg:p-6 text-center flex flex-col justify-center">
                  <div className="text-pink-500 text-xs lg:text-sm font-semibold mb-2 uppercase tracking-wider">
                    Der PKI-Modus
                  </div>
                  <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-2">
                    Dein persönliches Radio
                  </h3>
                  <p className="text-sm lg:text-base text-gray-600 leading-relaxed">
                    Beantworte ein paar einfache Fragen, und die schmalsoft-KI erstellt dir daraus dein perfektes personalisiertes Programm.
                  </p>
                </div>
              </div>
              )}
            </div>

            {/* Bottom Row: Status Text/Question + Mode Switch + PKI Toolbar */}
            <div className="toolbar-container">
              {/* Status Text / PKI Question */}
              {showClickToPlay ? (
                isStreamOffline ? (
                  <>
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-red-500 text-base lg:text-lg font-medium">Stream offline</span>
                  </>
                ) : (
                  <>
                    <span className="text-gray-700 text-base lg:text-lg font-medium">Klicke</span>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="lg:w-6 lg:h-6 flex-shrink-0 text-gray-700">
                      <path d="M8 5v14l11-7z" fill="currentColor" />
                    </svg>
                    <span className="hidden sm:inline text-gray-700 text-base lg:text-lg font-medium">
                      um <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-pink-500">schmalgsicht</span> zu hören
                    </span>
                  </>
                )
              ) : statusText ? (
                <div className="max-w-[50vw] sm:max-w-none overflow-hidden">
                  <div className="sm:hidden inline-flex animate-marquee whitespace-nowrap">
                    <span className="text-gray-700 text-base font-medium px-4">{statusText}</span>
                    <span className="text-gray-700 text-base font-medium px-4">{statusText}</span>
                  </div>
                  <span className="hidden sm:inline text-gray-700 lg:text-lg font-medium truncate">
                    {statusText}
                  </span>
                </div>
              ) : <span />}

              <div className="flex-1" />

              {/* PKI Toolbar (inside card) - Desktop only */}
              {isPKIActive && isAnalyzerActive && (
                <div className="hidden sm:block">
                  <PKIToolbar
                    onOpenTicTacToe={() => setActiveActivity('ticTacToe')}
                    onOpenDrawing={() => setActiveActivity('drawing')}
                    currentSection={currentSection}
                    scrollProgress={scrollProgress}
                  />
                </div>
              )}

              {/* Mode Switch */}
              <ModeSwitch mode={mode} onModeChange={onModeChange} />
            </div>
          </div>
        </div>

      {/* PKI Debug Panel */}
      <PKIDebugPanel
        profileId={currentProfileId}
        isVisible={isPKIActive}
        hasPlayedTicTacToe={hasPlayedTicTacToe}
        hasDrawn={hasDrawn}
      />
    </div>
  );
}
